"use client";

import { Heading } from "@/components/base/heading";
import { BookingNextActivitiesForFilters } from "@/components/booking/booking-next-activities/booking-next-activities-for-filters";
import { ClockIcon } from "@/components/icons/icons";
import { ContentContainer } from "@/components/layout/content-container";
import { FullWidthContainer } from "@/components/layout/full-width-container";
import { HorizontalSpacer } from "@/components/layout/horizontal-spacer";
import { VerticalSpacer } from "@/components/layout/vertical-spacer";
import { VisuallyHidden } from "@/components/layout/visually-hidden";
import { Breadcrumbs } from "@/components/navigation/breadcrumbs";
import { DynamicZone } from "@/components/ui/dynamic-zone";
import { HandleTextToRichText } from "@/components/ui/handle-text-to-richtext";
import { IconAndText } from "@/components/ui/icon-and-text";
import {
  MediaCard,
  type MediaCardHorizontalAlignmentType,
  type MediaCardVerticalAlignmentType,
} from "@/components/ui/media-card";
import type { TextTheme } from "@/components/ui/ui.model";
import { type ActivityFragment, FilterTypes } from "@/generated/client.generated";

interface ActivityProps {
  parts: ActivityFragment;
}

export const Activity = ({ parts }: ActivityProps) => {
  return (
    <>
      <VisuallyHidden>
        <Heading type="h1">{parts.page.title}</Heading>
      </VisuallyHidden>

      {parts.page?.responsiveMedia && (
        <>
          <FullWidthContainer spanTopMargin={true}>
            <MediaCard
              alignContentHorizontally={parts.page.alignContentHorizontally as MediaCardHorizontalAlignmentType}
              alignContentVertically={parts.page.alignContentVertically as MediaCardVerticalAlignmentType}
              buttonText={parts.page.headerButtonText ?? undefined}
              fallbackAlt={parts.page.title}
              heading={parts.page.headerHeading ?? undefined}
              media={parts.page.responsiveMedia}
              priority={true}
              text={parts.page.headerText ?? undefined}
              textTheme={parts.page.textTheme as TextTheme}
              url={parts.page.headerButtonUrl ?? undefined}
            />
          </FullWidthContainer>
          <HorizontalSpacer size="x-small" />
        </>
      )}

      <ContentContainer horizontalPlacement="left" width="medium">
        <Breadcrumbs breadcrumbs={parts.page.breadcrumbs} hideBreadcrumbs={parts.page.hideBreadcrumbs} />

        {!parts.page.headerHeading && (
          <>
            <Heading type="h2">{parts.page.title}</Heading>
            <HorizontalSpacer size="x-small" />
          </>
        )}

        {parts.durationText && (
          <>
            <IconAndText
              icon={
                <>
                  <ClockIcon stripIcon={true} />
                  <VerticalSpacer size="x-small" />
                </>
              }
              size="normal"
              text={parts.durationText}
            />
            <HorizontalSpacer size="large" />
          </>
        )}

        {(parts.page.description || parts.page.descriptionRichText) && <HandleTextToRichText page={parts.page} />}
      </ContentContainer>

      <DynamicZone blocks={parts.blocks} />
      <HorizontalSpacer size="large" />

      <BookingNextActivitiesForFilters
        currentFilters={parts.groupActivityIds.map((id) => {
          return {
            type: FilterTypes.GroupActivity,
            value: id,
          };
        })}
        take={10}
        showWeekDayFilter={true}
        title={`Kommende ${parts.page.title}-timer`}
      />
    </>
  );
};
